import { Breadcrumb } from '@shared/chrome/Breadcrumb';

import { copy } from './copy';
import { SurfaceSection } from './Shelves.SurfaceSection';
import { TagStripSection } from './Shelves.TagStripSection';
import { SHELF_SURFACES } from './types';

/**
 * Shelf management — the ordered shelf list of each of the two home surfaces,
 * with every shelf's title, source, the subset that source resolves against,
 * and its rank, plus create, edit, reorder, and a delete confirmed by name.
 *
 * **Two sections, one mechanism.** The public home and the subscriber home are
 * two compositions of the same shelf list rather than two implementations, so
 * both sections are the same component over a different surface — and each
 * holds its own read, so one being unreachable never blanks the other.
 *
 * What this page deliberately does not draw, and why:
 *
 * - **No layout picker and no layout column.** The catalog places a shelf's
 *   shape in its declared identity in configuration rather than in a runtime
 *   choice; the four layouts ship on the home page and are simply not this
 *   surface's to set.
 * - **No item count per shelf.** No row gives a shelf a size here, and the
 *   reporting dashboards count users, content, engagement and revenue — not
 *   shelf membership.
 * - **No preview strip.** It would pull the title card into an area whose
 *   inherited component set excludes every catalogue component: an operator
 *   surface that grew a poster rail would be showing the catalogue instead of
 *   managing it.
 * - **No active / inactive toggle.** No row gives a shelf an enabled state. The
 *   two viewer-scoped shelves are conditional by *source*, which is a different
 *   thing and is already said on the row.
 * - **No see-all URL field.** The link corresponds to the source, so it is
 *   derived rather than typed, and it renders as read-only text under the
 *   shelf's title.
 */
export function ShelvesPage() {
  return (
    <>
      <Breadcrumb items={[{ label: copy.crumbs.console, to: '/' }, { label: copy.crumbs.here }]} />

      <div className="mb-6 mt-5 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
      </div>

      {SHELF_SURFACES.map((surface) => (
        <SurfaceSection key={surface} surface={surface} />
      ))}

      {/*
        Last, and not per surface. The strip is ONE curated list drawn above
        every listing on both reader hosts, so a copy of it inside each section
        above would be two controls writing one value.
      */}
      <TagStripSection />
    </>
  );
}
